import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleEnterPress } from '../../shared/helpers/util';
import { setVolume } from '../reducers/audio';
import {
  attendedTransferCall,
  cancelTransfer,
  hideDialler,
  holdCall,
  initiateTransfer,
  muteCall,
  showDialler,
  unholdCall,
  unmuteCall,
} from '../reducers/calls';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/callControls.module.scss';
import Slider from './Slider';
import {
  DialpadIcon,
  HoldIcon,
  MuteIcon,
  TransferIcon,
  VolumeIcon,
} from './icons';

const CallControls: React.FC = () => {
  const dispatch = useDispatch<StoreDispatch>();
  const volume = useSelector((state: StoreState) => state.audio.volume);
  const calls = useSelector((state: StoreState) => state.calls);
  const call = calls.find((c) => c.active === true);
  const { id, onHold, muted, diallerVisible, transferInitiated } = call || {};
  const [showVolume, setShowVolume] = useState(false);
  const volumeToggleId = 'volume-toggle-control';

  // Transfer only ever targets an inbound call, so require at least one.
  const inboundCalls = calls.filter((c) => c.direction === 'IN' && c.answered);
  const transferEnabled = !!call?.answered && inboundCalls.length > 0;

  const onOutsideClick = useCallback(() => {
    setShowVolume(false);
  }, []);

  const holdCallClick = (id: string) => {
    dispatch(holdCall(id));
  };
  const unholdCallClick = (id: string) => {
    dispatch(unholdCall(id));
  };
  const muteCallClick = (id: string) => {
    dispatch(muteCall(id));
  };
  const unmuteCallClick = (id: string) => {
    dispatch(unmuteCall(id));
  };
  const transferClick = (id: string) => {
    dispatch(initiateTransfer({ id }));
  };
  const transferCancelClick = (id: string) => {
    dispatch(cancelTransfer({ id }));
  };
  const changeVolume = (volume: number) => {
    dispatch(setVolume(volume));
  };
  const showDiallerClick = (id: string) => {
    dispatch(showDialler({ id }));
  };
  const hideDiallerClick = (id: string) => {
    dispatch(hideDialler({ id }));
  };

  // FIXME is this correct?
  if (!id) {
    return null;
  }

  const toggleMute = () => {
    return muted ? unmuteCallClick(id) : muteCallClick(id);
  };

  const toggleShowVolume = () => {
    setShowVolume(!showVolume);
  };

  const toggleHold = () => {
    return onHold ? unholdCallClick(id) : holdCallClick(id);
  };
  const toggleDialer = () => {
    return diallerVisible ? hideDiallerClick(id) : showDiallerClick(id);
  };
  const toggleTransfer = () => {
    if (!transferEnabled || !id) {
      return;
    }
    if (call?.direction === 'IN') {
      return transferInitiated ? transferCancelClick(id) : transferClick(id);
    }
    // Outbound: attended transfer to an inbound call only, never blind.
    if (transferInitiated) {
      return transferCancelClick(id);
    }
    if (inboundCalls.length === 1) {
      // One target — transfer immediately, skip selection.
      dispatch(attendedTransferCall(inboundCalls[0].id));
      return;
    }
    return transferClick(id);
  };

  return (
    <div className={styles.container}>
      <span
        className={muted ? styles.control_active : styles.control}
        onClick={toggleMute}
        onKeyDown={handleEnterPress(toggleMute)}
        role='button'
        tabIndex={0}
      >
        <MuteIcon className={styles.icon} />
      </span>
      <span className={styles.volume_control}>
        <div
          className={styles.volume_clickable}
          id={volumeToggleId}
          onClick={toggleShowVolume}
          onKeyDown={handleEnterPress(toggleShowVolume)}
          role='button'
          tabIndex={0}
        />
        <div className={styles.volume_container}>
          <VolumeIcon className={styles.icon} />
          <Slider
            visible={showVolume}
            defaultValue={volume}
            onChange={(e) => {
              const { value } = e.target;
              // For some reason the value returned by the
              // slider is a string, so cast it to a float
              const floatValue = parseFloat(value);
              changeVolume(floatValue);
            }}
            onOutsideClick={onOutsideClick}
            ignoreId={volumeToggleId}
          />
        </div>
        <div
          className={styles.progress_border}
          style={{ width: `${volume * 100}%` }}
        />
      </span>
      <span
        className={onHold ? styles.control_active : styles.control}
        onClick={toggleHold}
        onKeyDown={handleEnterPress(toggleHold)}
        role='button'
        tabIndex={0}
      >
        <HoldIcon className={styles.icon} />
      </span>
      <span
        className={diallerVisible ? styles.control_active : styles.control}
        onClick={toggleDialer}
        onKeyDown={handleEnterPress(toggleDialer)}
        role='button'
        tabIndex={0}
      >
        <DialpadIcon className={styles.icon} />
      </span>
      {transferEnabled && (
        <span
          className={transferInitiated ? styles.control_active : styles.control}
          onClick={toggleTransfer}
          onKeyDown={handleEnterPress(toggleTransfer)}
          role='button'
          tabIndex={0}
        >
          <TransferIcon className={styles.icon} />
        </span>
      )}
    </div>
  );
};

export default CallControls;
